import iconsFacebookWhiteSvg from '@/assets/icons/facebookWhite.svg';
import iconsTwitterWhiteSvg from '@/assets/icons/twitter.svg';
import iconsTwitterDarkGreySvg from '@/assets/icons/twitterDarkGrey.svg';
import { getCdnUrl } from '@/utils/cdn';

export function getDatasetUrl(filename) {
  return getCdnUrl(`datasets/${filename}`);
}

export function getCdnAssetUrl(path) {
  return getCdnUrl(`images/assets/${path}`);
}

export function getImageSource(path) {
  return getCdnAssetUrl(path);
}

export function getSrcSet(folder) {
  return [...Array(3).keys()].map((i) => `${getImageSource(`${folder}/${i + 1}x.png`)} ${i + 1}x`);
}

/**
 * Creates an object with lazily-evaluated image URL properties.
 * URLs are only constructed when a property is first accessed,
 * avoiding eager CDN URL construction at module import time.
 *
 * @param {Object} definitions - Map of keys to:
 *   - string: path passed to getImageSource()
 *   - function: custom getter (for getCdnAssetUrl, getSrcSet, arrays, etc.)
 *   - object: nested lazy image map
 */
function lazyImages(definitions) {
  const obj = {};
  for (const [key, value] of Object.entries(definitions)) {
    if (typeof value === 'function') {
      Object.defineProperty(obj, key, { get: value, enumerable: true });
    } else if (typeof value === 'string') {
      Object.defineProperty(obj, key, {
        get: () => getImageSource(value),
        enumerable: true,
      });
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      obj[key] = lazyImages(value);
    }
  }
  return obj;
}

export const IMG = lazyImages({
  googleAnalytics: 'googleAnalytics.svg',
  google: 'logos/google.svg',
  connectionRevokedAvatar: 'connection-revoked-avatar@2x.png',
  creatorPaymentsBanner: 'creator-payments-banner.png',
  igMessageEnableAccess: 'ig-message-enable-access.png',
  twitterMessageEnableAccess: 'twitter-message-enable-access.png',
  dashboardsPreviewCompetitiveReport: 'dashboards/preview-competitive-report.png',
  dashboardsPreviewContentReport: 'dashboards/preview-contentreport.png',
  dashboardsCompetitorGraphPreview: 'dashboards/competitor-graph-preview.png',
  dashboardsGraphSingleLine: 'dashboards/graph_single-line.png',
  dashboardsSingleBarGraphPreview: 'dashboards/single-bar-graph-preview.png',
  dashboardsStackedBarGraphPreview: 'dashboards/stacked-bar-graph-preview.png',
  dashboardsPreviewGraph: 'dashboards/preview-graph.png',
  dashboardsMultipleBrandGraph: 'dashboards/multi-graph-preview.png',
  emptyThumbup: 'empty-thumbup.png',
  atSymbol: 'at-symbol.svg',
  friends: 'friends.png',
  checkMarkBlue: 'checkmark-blue.svg',
  logosTiktokTrendingSounds: 'logos/tiktok-trending-sounds.svg',
  soundsList: 'sounds-list.svg',
  emptySeashore: 'empty-seashore.png',
  chromeExtension: 'chrome-extension.png',
  engageVisual: 'engage-visual.png',
  importing: 'importing.png',
  arrowLeft: 'arrow-left.png',
  arrowRight: 'arrow-right.png',
  iconsTwitterWhite: () => iconsTwitterWhiteSvg,
  iconsFacebookWhite: () => iconsFacebookWhiteSvg,
  iconsTwitterDarkGrey: () => iconsTwitterDarkGreySvg,
  emptyKanpai: 'empty-kanpai.png',
  emptyWave: 'empty-wave@2x.png',
  emptyCattub: 'empty-cattub.png',
  emptyBeach: 'empty-beach.png',
  emptyField: 'empty-field@2x.png',
  radioButtonOn: 'radioButtonOn.svg',
  radioButtonOff: 'radioButtonOff.svg',
  beisLogo: 'inAppTrials/beis-logo.png',
  toryBurchLogo: 'inAppTrials/tory-burch-logo.png',
  coterieLogo: 'inAppTrials/coterie-logo.png',
  stellMcCartneyLogo: 'inAppTrials/stellmccartney-logo.png',
  summerFridaysLogo: 'inAppTrials/summerfridays-logo.png',
  tiktokCreatorOverviewBanner: 'tiktok-creator-overview-banner.png',
  instagramCreatorOverviewBanner: 'instagram-creator-overview-banner.png',
  emailCreatorOverviewBanner: 'email-creator-overview-banner.png',
  firstTimeUserBanner: 'first-time-user-banner.png',
  tiktokUgcDisconnected: 'tiktokUgcDisconnected/1x.png',
  tiktokUgcUpgrade: 'tiktokUgcUpgrade/1x.png',
  addFacebookProductCatalog: 'products/add-facebook-product-catalog/1x.png',
  salesforceIntegrationBanner: 'salesforce-connection.png',
  addProductLinks: 'products/add-product-links/1x.png',
  boards: {
    emptyBoard: 'boards/empty-board/3x.png',
  },
  galleries: {
    emptyGallery: 'galleries/empty-gallery/3x.png',
  },
  dashboard: {
    template: {
      blank: 'dashboards/templates/blank-overview/1x.png',
      socialOverview: 'dashboards/templates/social-competitive-overview/1x.png',
      competitiveOverview: 'dashboards/templates/social-competitive-overview/1x.png',
      brandHealth: 'dashboards/templates/brand-health-overview/1x.png',
      tiktokOverview: 'dashboards/templates/tiktok-overview/1x.png',
      instagramOverview: 'dashboards/templates/instagram-overview/1x.png',
      facebookOverview: 'dashboards/templates/facebook-overview/1x.png',
      pinterestOverview: 'dashboards/templates/pinterest-overview/1x.png',
      xOverview: 'dashboards/templates/x-overview/1x.png',
      youtubeOverview: 'dashboards/templates/blank-overview/1x.png',
      linkedinOverview: 'dashboards/templates/linkedin-overview/1x.png',
      creatorsOverview: 'dashboards/templates/creator-overview/1x.png',
      dashboardOverview: 'dashboards/templates/dashboard-overview/3x.png',
      communityOverview: 'dashboards/templates/community-overview/1x.png',
      listeningOverview: 'dashboards/templates/listening-overview/1x.png',
    },
    noReports: {
      competitive: 'dashboards/noReports/competitive-report/1x.png',
      industryEarned: 'dashboards/noReports/industry-earned-report/1x.png',
      creatorsEarned: 'dashboards/noReports/creators-earned-report/1x.png',
      owned: 'dashboards/noReports/owned-report/1x.png',
      socialAdvertising: 'dashboards/noReports/social-advertising-report/1x.png',
      community: 'dashboards/noReports/community-report/1x.png',
      socialListening: 'dashboards/noReports/social-listening-report/1x.png',
    },
  },
  campaigns: {
    emptyCampaignAddMedia: 'campaigns/empty-campaign-add-media/1x.png',
    emptyCampaignAddBoards: 'campaigns/empty-campaign-add-boards/1x.png',
    emptyCampaignAddCreators: 'campaigns/empty-campaign-add-creators/1x.png',
    emptyCampaignAddAdCampaigns: 'campaigns/empty-campaign-add-ad-campaigns/1x.png',
    emptyCampaignsWithAccess: 'campaigns/empty-campaigns-with-access/1x.png',
    socialCommerceNoConnection: 'campaigns/social-commerce-no-connection/1x.png',
    benchmarks: 'campaigns/benchmarks-and-goals/benchmarks/1x.png',
    goals: 'campaigns/benchmarks-and-goals/goals/1x.png',
    name: 'campaigns/benchmarks-and-goals/name/1x.png',
    new: {
      emptyCampaignAddMedia: 'campaigns-new/empty-campaign-add-media/1x.png',
      emptyCampaignAddBoards: 'campaigns-new/empty-campaign-add-boards/1x.png',
      emptyCampaignAddCreators: 'campaigns-new/empty-campaign-add-creators/1x.png',
      emptyCampaignAddAdCampaigns: 'campaigns-new/empty-campaign-add-ad-campaigns/1x.png',
      emptyCampaignCreatorChannels: 'campaigns-new/empty-campaign-creator-channels/1x.png',
    },
  },
  creators: {
    emptyYourCreators: 'creators/empty-your-creators/1x.png',
    shopifyConnection: 'creators/shopify-connection/1x.png',
    bitlyConnection: 'creators/bitly-connection/1x.png',
    connectSocials: 'creators/connect-socials/1x.png',
    googleAnalyticsConnection: 'creators/google-analytics-connection/1x.png',
  },
  library: {
    emptyLibraryUploadMedia: 'library/empty-library-upload-media/1x.png',
    emptyLibraryConnectAccounts: 'library/empty-library-connect-accounts/1x.png',
  },
  contentAutomation: {
    brandSelection: 'content-automation/brand-selection/1x.png',
    brandSelectionSrcset: () => getSrcSet('content-automation/brand-selection'),
  },
  snapchat: {
    connection: 'snapchat/connection/1x.png',
    connectionSrcset: () => getSrcSet('snapchat/connection'),
  },
});

export const inAppTrials = lazyImages({
  engage: () => [
    getImageSource('inAppTrials/engage/3x.png'),
    getImageSource('inAppTrials/engage/2x.png'),
    getImageSource('inAppTrials/engage/1x.png'),
  ],
  advance: () => [
    getImageSource('inAppTrials/advance/3x.png'),
    getImageSource('inAppTrials/advance/2x.png'),
    getImageSource('inAppTrials/advance/1x.png'),
  ],
  premium: () => [
    getImageSource('inAppTrials/premium/3x.png'),
    getImageSource('inAppTrials/premium/2x.png'),
    getImageSource('inAppTrials/premium/1x.png'),
  ],
  socialListening: () => [
    getImageSource('inAppTrials/socialListening/3x.png'),
    getImageSource('inAppTrials/socialListening/2x.png'),
    getImageSource('inAppTrials/socialListening/1x.png'),
  ],
  creators: () => [
    getImageSource('inAppTrials/creators/3x.png'),
    getImageSource('inAppTrials/creators/2x.png'),
    getImageSource('inAppTrials/creators/1x.png'),
  ],
});
